import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import type { ManagedUser, Role } from './UsersPage';

type AccessRow = { entity_id: number; access_level: string; can_see_private: boolean };

/**
 * Cadastro de usuário — RF-AUT.
 *
 * As duas camadas ficam no mesmo formulário, uma abaixo da outra. Papel e
 * acesso são obrigatórios juntos: conceder um sem o outro produz um usuário que
 * entra e vê o sistema vazio — e ninguém descobre isso lendo o cadastro dele.
 */
export function UserForm({
  user,
  roles,
  onClose,
  onSaved,
}: {
  user: ManagedUser | null;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [status, setStatus] = useState(user?.status ?? 'ativo');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [roleIds, setRoleIds] = useState<number[]>(user?.roles.map((r) => r.id) ?? []);
  const [access, setAccess] = useState<AccessRow[]>(
    user?.entity_access.map((a) => ({
      entity_id: a.entity_id,
      access_level: a.access_level,
      can_see_private: a.can_see_private,
    })) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const reference = useQuery({
    queryKey: ['reference', []],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference'),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim(),
        status,
        role_ids: roleIds,
        entity_access: access,
      };

      if (!user) {
        payload.password = password;
        payload.password_confirmation = confirmation;
      }

      return user ? api.put(`/users/${user.id}`, payload) : api.post('/users', payload);
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError) {
        setError(Object.values(e.errors ?? {}).flat()[0] ?? e.message);
        return;
      }
      setError('Não foi possível salvar o usuário.');
    },
  });

  const entities = reference.data?.entities ?? [];

  function toggleEntity(entityId: number) {
    setAccess((rows) =>
      rows.some((r) => r.entity_id === entityId)
        ? rows.filter((r) => r.entity_id !== entityId)
        : [...rows, { entity_id: entityId, access_level: 'escrita', can_see_private: false }],
    );
  }

  function updateAccess(entityId: number, patch: Partial<AccessRow>) {
    setAccess((rows) => rows.map((r) => (r.entity_id === entityId ? { ...r, ...patch } : r)));
  }

  const mismatch = confirmation.length > 0 && password !== confirmation;

  return (
    <Modal title={user ? `Editar ${user.name}` : 'Novo usuário'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        className="space-y-3.5"
      >
        <Field label="Nome" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={180}
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="E-mail" required hint="É com ele que a pessoa entra.">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={180}
              autoComplete="off"
              className="input w-full"
            />
          </Field>

          <Field label="Situação">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="input w-full">
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo — não entra</option>
              {user && <option value="bloqueado">Bloqueado</option>}
            </select>
          </Field>
        </div>

        {/* A senha só aparece na criação. Editar não é lugar de trocar senha —
            para isso existe "Redefinir senha", que é uma ação com consequência
            própria e fica na auditoria. */}
        {!user && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Senha inicial" required hint="Mínimo 12 caracteres.">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
                className="input w-full"
              />
            </Field>

            <Field label="Repita a senha" required>
              <input
                type="password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                required
                autoComplete="new-password"
                className="input w-full"
              />
            </Field>
          </div>
        )}

        {mismatch && (
          <p className="text-xs text-rose-600 dark:text-rose-400">As duas senhas não coincidem.</p>
        )}

        {/* --- Camada 1: o que pode fazer --- */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Papel — o que pode fazer
          </p>

          <div className="mt-2 space-y-1.5">
            {roles.map((r) => (
              <label key={r.id} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={roleIds.includes(r.id)}
                  onChange={() =>
                    setRoleIds((ids) =>
                      ids.includes(r.id) ? ids.filter((x) => x !== r.id) : [...ids, r.id],
                    )
                  }
                  className="mt-0.5 rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                />
                <span>
                  {r.label}
                  {r.description && (
                    <span className="block text-xs text-slate-400">{r.description}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* --- Camada 2: sobre quais dados --- */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Entidades — sobre quais dados
          </p>

          {access.length === 0 && (
            <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
              Sem nenhuma entidade marcada, este usuário entra e não vê nada.
            </p>
          )}

          <div className="mt-2 space-y-2">
            {entities.map((e) => {
              const row = access.find((a) => a.entity_id === e.id);

              return (
                <div key={e.id} className="rounded-lg border border-slate-100 p-2 dark:border-slate-800">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!row}
                      onChange={() => toggleEntity(e.id)}
                      className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                    />
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: e.color ?? '#94a3b8' }}
                    />
                    {e.name}
                  </label>

                  {row && (
                    <div className="mt-2 flex flex-wrap items-center gap-3 pl-6">
                      <select
                        value={row.access_level}
                        onChange={(ev) => updateAccess(e.id, { access_level: ev.target.value })}
                        className="input py-1 text-xs"
                      >
                        <option value="leitura">Só consultar</option>
                        <option value="escrita">Consultar e lançar</option>
                        <option value="total">Total</option>
                      </select>

                      <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={row.can_see_private}
                          onChange={(ev) => updateAccess(e.id, { can_see_private: ev.target.checked })}
                          className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                        />
                        {/* O nome do campo não explica o alcance; o texto explica. */}
                        Vê lançamentos marcados como privados
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={
              save.isPending ||
              name.trim() === '' ||
              email.trim() === '' ||
              roleIds.length === 0 ||
              access.length === 0 ||
              (!user && (password.length < 12 || mismatch))
            }
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
